import { getPeople, getPlan, NotFound, type People, type Plan } from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { SharedCosts } from './SharedCosts';
import { Travellers } from '../../components/travellers/Travellers';
import { useLive } from '../../app/live';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; people: People };

/**
 * Who travels and what it costs them, as Tricount keeps it: the trip's people, those along
 * without an account, and the costs shared. Who sees the trip is the bar's Share.
 */
export function Group() {
  const { t } = useTranslation();
  const ids = useId();
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  // The fellow travellers without an account, beside the trip's members: on the plan.
  const [plan, setPlan] = useState<Plan | null>(null);

  useEffect(() => {
    getPlan(id).then(setPlan, () => undefined);
  }, [id, changed]);

  useEffect(() => {
    getPeople(id).then(
      (people) => {
        setState({ kind: 'ready', people });
      },
      (error: unknown) => {
        setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
  }, [id, changed]);

  if (state.kind === 'loading') {
    return <p className="page quiet">{t('trips.loading')}</p>;
  }
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }

  const { people } = state;
  return (
    <div className="group-page">
      <h1 className="visually-hidden">{t('group.title')}</h1>
      <div className="group-columns">
        <div className="group-left">
          <section className="panel" aria-labelledby={`${ids}-people`}>
            <div className="panel-head">
              <h2 id={`${ids}-people`}>{t('group.people')}</h2>
              <Link className="button button-small" to={`/trips/${id}/sharing`}>
                {t(people.role === 'owner' ? 'group.manage' : 'group.access')}
              </Link>
            </div>
            <ul className="people">
              {people.members.map((member) => (
                <li key={member.userId}>
                  <span className="person">
                    <span className="avatar" aria-hidden="true">
                      {member.name.slice(0, 1).toUpperCase()}
                    </span>
                    <strong>{member.name}</strong>
                  </span>
                  <span className="role-tag">{t(`account.role.${member.role}`)}</span>
                </li>
              ))}
            </ul>
          </section>
          {plan && <Travellers plan={plan} onSaved={setPlan} />}
        </div>
        <div className="group-center">
          {people.role !== 'viewer' && <SharedCosts trip={id} members={people.members} />}
        </div>
      </div>
    </div>
  );
}
